import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom'
import React, { useState, useEffect, Suspense, lazy } from 'react'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './contexts/ToastContext'
import { ClinicalAlertsProvider } from './contexts/ClinicalAlertsContext.jsx'
// MainLayout will be lazy loaded below

// Lazy load all pages for better performance
const MainLayout = lazy(() => import('./components/layout/MainLayout'));
const SuperAdminLayout = lazy(() => import('./components/layout/SuperAdminLayout'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdmin/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Patients = lazy(() => import('./pages/Patients'));
const PatientProfile = lazy(() => import('./pages/PatientProfile'));
const Interventions = lazy(() => import('./pages/Interventions'));
const Medications = lazy(() => import('./pages/Medications'));
const AIAssistant = lazy(() => import('./pages/AIAssistant'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const Settings = lazy(() => import('./pages/Settings'));
const MedicationErrors = lazy(() => import('./pages/MedicationErrors'));
const MedicationErrorsAnalytics = lazy(() => import('./pages/MedicationErrorsAnalytics'));
const Charts = lazy(() => import('./pages/Charts'));
const Calculators = lazy(() => import('./pages/Calculators'));
const CalculatorDetail = lazy(() => import('./pages/CalculatorDetail'));
const VancomycinCalculator = lazy(() => import('./pages/Calculators/VancomycinCalculator'));
const TPNCalculator = lazy(() => import('./pages/Calculators/TPNCalculator'));
const PVDashboard = lazy(() => import('./pages/PV/Dashboard'));
const ReportsList = lazy(() => import('./pages/PV/ReportsList'));
const ReportView = lazy(() => import('./pages/PV/ReportView'));
const NewReport = lazy(() => import('./pages/PV/NewReport'));
const Antibiogram = lazy(() => import('./pages/Antibiogram'));
const UserKPI = lazy(() => import('./pages/UserKPI'));
const Lexicomp = lazy(() => import('./pages/Lexicomp'));
const DrugBank = lazy(() => import('./pages/DrugBank'));
const ASPDashboard = lazy(() => import('./pages/ASP/ASPDashboard'));
const MedRec = lazy(() => import('./pages/MedRec'));

const ProtocolManagement = lazy(() => import('./pages/Admin/ProtocolManagement'));
const Worklist = lazy(() => import('./components/dashboard/Worklist'));
const Analytics = lazy(() => import('./components/dashboard/Analytics'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const IVCompatibility = lazy(() => import('./pages/Calculators/IVCompatibility'));

// Training Suite
const TrainingComingSoon = lazy(() => import('./pages/Training/ComingSoon'));
const TrainingDashboard = lazy(() => import('./pages/Training/TrainingDashboard'));

// Specialized Clinical Tools
const TDMLab = lazy(() => import('./pages/Clinical/TDMLab'));
const ROITracker = lazy(() => import('./pages/Clinical/ROITracker'));

// Marketing Pages
import MarketingLayout from './components/marketing/MarketingLayout';
import Home from './pages/marketing/Home';
const Features = lazy(() => import('./pages/marketing/Features'));
const Pricing = lazy(() => import('./pages/marketing/Pricing'));
const About = lazy(() => import('./pages/marketing/About'));
const Contact = lazy(() => import('./pages/marketing/Contact'));
const ClinicalCalculator = lazy(() => import('./pages/marketing/ClinicalCalculator'));
const Playground = lazy(() => import('./pages/Playground'));
const ClinicalPearls = lazy(() => import('./pages/marketing/ClinicalPearls'));
const PearlDetail = lazy(() => import('./pages/marketing/PearlDetail'));
const DocumentIngest = lazy(() => import('./components/ai/DocumentIngest'));
const PrivacyPolicy = lazy(() => import('./pages/marketing/PrivacyPolicy'));
const Documentation = lazy(() => import('./pages/marketing/Documentation'));
const UserGuide = lazy(() => import('./pages/marketing/docs/UserGuide'));
const ApiReference = lazy(() => import('./pages/marketing/docs/ApiReference'));
const ClinicalTools = lazy(() => import('./pages/marketing/docs/ClinicalTools'));
const AIHub = lazy(() => import('./pages/marketing/AIHub'));
const AIChatbot = lazy(() => import('./pages/marketing/AIChatbot'));
const AntibiogramLanding = lazy(() => import('./pages/marketing/AntibiogramLanding'));
const AILanding = lazy(() => import('./pages/marketing/AILanding'));
const RenalLanding = lazy(() => import('./pages/marketing/RenalLanding'));
const ClinicalDocs = lazy(() => import('./pages/ClinicalDocs'));
const TermsOfService = lazy(() => import('./pages/marketing/TermsOfService'));
const SystemStatus = lazy(() => import('./pages/marketing/SystemStatus'));
const CommunityFeed = lazy(() => import('./pages/marketing/CommunityFeed'));
const CommunityPostDetail = lazy(() => import('./pages/marketing/CommunityPostDetail'));
import CreatePost from './pages/marketing/CreatePost';

import PaymentTest from './components/payment/PaymentTest'; // Temp Import

const ChatbotWidget = lazy(() => import('./components/marketing/ChatbotWidget'));


import FeatureGuard from './components/common/FeatureGuard'
import ThemeSync from './components/common/ThemeSync'
import ScrollToTop from './components/common/ScrollToTop'

import { RoundsProvider } from './contexts/RoundsContext.jsx'

/** Align stored session with product default: dark unless profile explicitly chose light/dark. */
const normalizeStoredUser = (user) => {
  if (!user || typeof user !== 'object') return user
  const existing = user.preferences && typeof user.preferences === 'object' ? user.preferences : {}
  const raw = existing.theme
  const theme = raw === 'light' || raw === 'dark' ? raw : 'dark'
  return {
    ...user,
    preferences: { ...existing, theme }
  }
}

const THEME_STORAGE_KEY = 'medcorex_theme'

function readPersistedSessionUser() {
  try {
    const savedUser = localStorage.getItem('user')
    if (!savedUser) return null
    const parsed = JSON.parse(savedUser)
    const existing = parsed.preferences && typeof parsed.preferences === 'object' ? parsed.preferences : {}
    const raw = existing.theme
    const profileTheme = raw === 'light' || raw === 'dark' ? raw : null
    let lsTheme = null
    try {
      const ls = localStorage.getItem(THEME_STORAGE_KEY)
      if (ls === 'light' || ls === 'dark') lsTheme = ls
    } catch {
      /* ignore */
    }
    const theme = lsTheme ?? profileTheme ?? 'dark'
    const user = {
      ...parsed,
      preferences: { ...existing, theme }
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      /* ignore */
    }
    return user
  } catch (error) {
    console.error('Error loading user:', error)
    return null
  }
}

// Analytics Tracker Component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
};

// Route Normalizer (Fixes double slashes)
const PathNormalizer = () => {
  const location = useLocation();
  if (location.pathname.includes('//')) {
    return <Navigate to={location.pathname.replace(/\/+/g, '/')} replace />;
  }
  return null;
};

// Global Chatbot Wrapper
const GlobalChatbot = () => {
  const location = useLocation();
  // Don't show on dashboard, tools, or auth pages
  const hidePaths = ['/dashboard', '/login', '/admin', '/patients', '/worklist', '/interventions', '/analytics', '/medications', '/ai-assistant', '/clinical-docs', '/settings', '/profile', '/hospital', '/charts', '/calculators', '/pv', '/antibiogram', '/asp', '/medrec', '/data-ingestion', '/lexicomp', '/drug-bank', '/user-kpi', '/verify-email', '/reset-password'];

  const shouldHide = hidePaths.some(path => location.pathname.startsWith(path));

  if (shouldHide || location.pathname.startsWith('/super-admin')) return null;
  return (
    <Suspense fallback={null}>
      <ChatbotWidget />
    </Suspense>
  );
};

function App() {
  const [user, setUser] = useState(() => readPersistedSessionUser())

  const handleLogin = (userData, token) => {
    const normalized = normalizeStoredUser(userData)
    setUser(normalized)
    localStorage.setItem('user', JSON.stringify(normalized))
    const t = normalized.preferences?.theme
    if (t === 'light' || t === 'dark') {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, t)
      } catch {
        /* ignore */
      }
    }
    if (token) {
      localStorage.setItem('token', token)
    }
  }

  const handleLogout = () => {
    setUser(null)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
  }

  const handleUpdateUser = (updatedUser) => {
    const normalized = normalizeStoredUser(updatedUser)
    setUser(normalized)
    localStorage.setItem('user', JSON.stringify(normalized))
    const t = normalized.preferences?.theme
    if (t === 'light' || t === 'dark') {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, t)
      } catch {
        /* ignore */
      }
    }
  }

  // Helper to wrap pages
  const ProtectedRoute = ({ children }) => {
    if (!user) return <Navigate to="/login" replace />
    return (
      <MainLayout user={user} onLogout={handleLogout}>
        {children}
      </MainLayout>
    )
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <ThemeSync user={user} />
        <RoundsProvider>
          <ClinicalAlertsProvider>
            <Router>
              <ScrollToTop />
              <AnalyticsTracker />
              <PathNormalizer />
              <GlobalChatbot />
              <Suspense fallback={<div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>}>
                <Routes>
                <Route
                  path="/login"
                  element={
                    user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
                  }
                />
                <Route path="/register" element={<Navigate to="/login?mode=register" replace />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* Protected Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard user={user} /></ProtectedRoute>} />
                {/* Admin Routes (Moved/Consolidated below for strict role enforcement) */}
                <Route path="/admin" element={<Navigate to="/hospital-dashboard" replace />} />
                {/* Super Admin Routes */}
                <Route path="/super-admin/*" element={
                  <ProtectedRoute>
                    {['admin','super_admin'].includes(user?.role) ? (
                      <SuperAdminLayout user={user} onLogout={handleLogout}>
                        <Routes>
                          <Route path="/" element={<SuperAdminDashboard user={user} />} />
                          <Route path="/manage" element={<AdminDashboard user={user} />} />
                        </Routes>
                      </SuperAdminLayout>
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </ProtectedRoute>
                } />

                <Route path="/admin/protocols" element={
                  <ProtectedRoute>
                    {['admin','super_admin'].includes(user?.role) ? <ProtocolManagement user={user} /> : <Navigate to="/dashboard" replace />}
                  </ProtectedRoute>
                } />
                <Route path="/audit-logs" element={
                  <ProtectedRoute>
                    {['admin','super_admin'].includes(user?.role) ? <AuditLogs /> : <Navigate to="/dashboard" replace />}
                  </ProtectedRoute>
                } />
                <Route path="/patients" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_patients">
                      <Patients user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/patient/new" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="manage_patients">
                      <PatientProfile user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/patient/:id" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_patients">
                      <PatientProfile user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                {/* Dashboard Features */}
                <Route path="/worklist" element={
                  <ProtectedRoute>
                    <Worklist user={user} />
                  </ProtectedRoute>
                } />

                <Route path="/interventions" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_interventions">
                      <Interventions user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="analytics">
                      <Analytics user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/analytics-dashboard" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="analytics">
                      <AnalyticsDashboard />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/medications" element={<ProtectedRoute><Medications user={user} /></ProtectedRoute>} />
                <Route path="/ai-assistant" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="ai_consult">
                      <AIAssistant user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/clinical-docs" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="ai_consult">
                      <ClinicalDocs />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><UserProfile user={user} onUpdateUser={handleUpdateUser} onLogout={handleLogout} /></ProtectedRoute>} />

                {/* Hospital Dashboard Redirect for specific role check if needed */}
                {/* Hospital Dashboard (Restored for Org Admin) */}
                <Route path="/hospital-dashboard"
                  element={
                    user && (['admin','super_admin','org_admin'].includes(user.role)) ? (
                      <MainLayout user={user} onLogout={handleLogout}>
                        <AdminDashboard user={user} />
                      </MainLayout>
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />

                <Route path="/medication-errors" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_med_errors">
                      <MedicationErrors user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/medication-errors/analytics" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_med_errors">
                      <MedicationErrorsAnalytics user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />

                <Route path="/charts" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="analytics">
                      <Charts user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />

                <Route path="/calculators" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_calculators">
                      <Calculators user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/calculators/iv-compatibility" element={<ProtectedRoute><IVCompatibility user={user} /></ProtectedRoute>} />
                <Route path="/calculators/vancomycin" element={<ProtectedRoute><VancomycinCalculator user={user} /></ProtectedRoute>} />
                <Route path="/calculators/tpn" element={<ProtectedRoute><TPNCalculator user={user} /></ProtectedRoute>} />
                <Route path="/calculators/:id" element={<ProtectedRoute><CalculatorDetail user={user} /></ProtectedRoute>} />

                <Route path="/pv" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="advanced_reports">
                      <PVDashboard user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/pv/reports/new" element={<ProtectedRoute><NewReport user={user} /></ProtectedRoute>} />
                <Route path="/pv/reports/:id" element={<ProtectedRoute><ReportView user={user} /></ProtectedRoute>} />
                <Route path="/pv/reports" element={<ProtectedRoute><ReportsList user={user} /></ProtectedRoute>} />
                <Route path="/pv/new-report" element={<ProtectedRoute><NewReport user={user} /></ProtectedRoute>} />

                <Route path="/antibiogram" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_antibiogram">
                      <Antibiogram user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />

                <Route path="/asp" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_antibiogram">
                      <ASPDashboard user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />

                <Route path="/medrec" element={<ProtectedRoute><MedRec /></ProtectedRoute>} />

                {/* Training Lab Routes - Locked for Development */}
                <Route path="/training/*" element={<ProtectedRoute><TrainingComingSoon user={user} /></ProtectedRoute>} />

                {/* Specialized Clinical Routes */}
                <Route path="/clinical/tdm-lab" element={<ProtectedRoute><TDMLab user={user} /></ProtectedRoute>} />
                <Route path="/clinical/roi-tracker" element={<ProtectedRoute><ROITracker user={user} /></ProtectedRoute>} />

                <Route path="/user-kpi" element={<ProtectedRoute><UserKPI user={user} /></ProtectedRoute>} />
                <Route path="/lexicomp" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_drug_resources">
                      <Lexicomp />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />
                <Route path="/drug-bank" element={
                  <ProtectedRoute>
                    <FeatureGuard user={user} feature="access_drug_resources">
                      <DrugBank user={user} />
                    </FeatureGuard>
                  </ProtectedRoute>
                } />

                {/* Marketing Routes */}
                <Route path="/ai-hub" element={
                  <MarketingLayout title="Clinical tools hub" description="Explore MedCoreX clinical tools—documentation, analysis, and safety workflows in one place.">
                    <AIHub />
                  </MarketingLayout>
                } />
                <Route path="/cortex" element={
                  <MarketingLayout title="Clinical assistant" description="Ask structured clinical questions—answers are informational and must be verified against local policy.">
                    <AIChatbot />
                  </MarketingLayout>
                } />
                <Route path="/smart-analyzer" element={
                  <MarketingLayout title="Smart Rx Analyzer" description="Structured medication record analysis and reporting—built for pharmacy teams.">
                    <AnalyticsDashboard isPublic={true} user={user} />
                  </MarketingLayout>
                } />
                <Route path="/medrec-standalone" element={
                  <MarketingLayout title="Vision MedRec" description="OCR-assisted extraction for medication lists—review and sign off in your workflow.">
                    <MedRec isStandalone={true} user={user} />
                  </MarketingLayout>
                } />
                <Route path="/docs-ai" element={
                  <MarketingLayout title="Document intelligence" description="Search and summarize clinical documents with traceable excerpts.">
                    <ClinicalDocs />
                  </MarketingLayout>
                } />
                <Route path="/antibiogram-demo" element={
                  <MarketingLayout title="Dynamic antibiogram" description="Local resistance patterns and stewardship-aligned suggestions—always confirm with microbiology.">
                    <Antibiogram isPublic={true} user={user} />
                  </MarketingLayout>
                } />
                <Route path="/playground" element={<Playground onLogin={handleLogin} />} />
                <Route path="/demo" element={<Navigate to="/playground" replace />} />
                <Route path="/clinical-pearls" element={<ClinicalPearls />} />
                <Route path="/clinical-pearls/:id" element={<PearlDetail />} />
                <Route path="/" element={<Home />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/calculator" element={<ClinicalCalculator />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/status" element={<SystemStatus />} />
                <Route path="/docs" element={<Documentation />} />
                <Route path="/docs/user-guide" element={<UserGuide />} />
                <Route path="/docs/api-reference" element={<ApiReference />} />
                <Route path="/docs/clinical-tools" element={<ClinicalTools />} />
                <Route path="/product/antibiogram" element={<AntibiogramLanding />} />
                <Route path="/product/ai" element={<AILanding />} />
                <Route path="/product/renal" element={<RenalLanding />} />
                <Route path="/data-ingestion" element={<ProtectedRoute><DocumentIngest /></ProtectedRoute>} />
                <Route path="/community" element={<CommunityFeed />} />
                <Route path="/community/new" element={<CreatePost />} />
                <Route path="/community/:id" element={<CommunityPostDetail />} />
                <Route path="/payment-test" element={<PaymentTest />} />

                {/* 404 Route */}
                <Route path="*" element={
                  <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-900 dark:text-white p-8">
                    <h1 className="text-6xl font-black text-rose-500 mb-4">404</h1>
                    <p className="text-xl text-slate-700 dark:text-slate-400 mb-8">Page not found or restricted access.</p>
                    <Link to="/dashboard" className="px-8 py-3 bg-emerald-500 rounded-2xl font-bold">Back to Dashboard</Link>
                  </div>
                } />
                </Routes>
              </Suspense>

            </Router>
          </ClinicalAlertsProvider>
        </RoundsProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

export default App

